import { Input } from '@commenda/kakashi';
import { TEquityInfo } from '@commenda/types/src/incorporation/types';
import React from 'react';

type CompanyValuationInputProps = {
  equityInfo: Partial<TEquityInfo>;
  setEquityInfo: React.Dispatch<React.SetStateAction<TEquityInfo>>;
  paidUpCapital: number;
  setPaidUpCapital: React.Dispatch<React.SetStateAction<number>>;
};

const regexThatMatchesNumberWithCommas = /^[0-9][0-9,]*$/;

const CompanyValuationSection = ({
  equityInfo,
  setEquityInfo,
  paidUpCapital,
  setPaidUpCapital,
}: CompanyValuationInputProps) => {
  return (
    <div className="flex flex-col gap-6 border-b pb-12">
      <div>
        <h3 className="text-base">Paid up capital</h3>
        <span className="text-sm text-neutral-2">
          We suggest you to keep the following default values to reduce your tax
          burden.
        </span>
      </div>
      <div>
        <Input
          className="hide-arrow pl-14"
          type="number"
          name="paid-up-capital"
          id="paid-up-capital"
          min={0}
          maxLength={9}
          placeholder="50,000"
          value={paidUpCapital}
          leftEl={() => <span className="flex pl-3">AED</span>}
          onChange={(e) => {
            const currentValue = e.target.value;
            if (currentValue.includes('.')) {
              const [intPart, decimalPart] = currentValue.split('.');
              if (decimalPart.length > 2) return;
              const formattedIntPart = intPart.replace(/^0+/, '') || '0';
              e.target.value = `${formattedIntPart}.${decimalPart}`;
            } else {
              // For non-decimal numbers, remove leading zeros but allow a single zero
              e.target.value = currentValue.replace(/^0+/, '') || '0';
            }
            const newValue = parseFloat(e.target.value);

            if (
              !Number.isNaN(newValue) &&
              newValue >= 0 &&
              newValue <= 100000000
            ) {
              setPaidUpCapital(newValue);
            }
          }}
        />
      </div>
      <div className="flex flex-col gap-4 mt-4 bg-bg-secondary px-5 py-6 rounded-md">
        <div className="flex items-center gap-6">
          <div className="flex flex-col gap-1 shrink-0 grow">
            <Input
              type="text"
              maxLength={15}
              placeholder="100"
              value={equityInfo?.shareCount?.toLocaleString()}
              onChange={(e) => {
                const newValue = e.currentTarget.value;
                if (!newValue) {
                  setEquityInfo((current) => ({
                    ...current,
                    shareCount: undefined,
                  }));
                } else if (regexThatMatchesNumberWithCommas.test(newValue)) {
                  setEquityInfo((current) => ({
                    ...current,
                    shareCount: parseInt(newValue.replaceAll(',', ''), 10),
                  }));
                }
              }}
            />
          </div>
          <p className="txt-medium-regular shrink-0">Issued Shares valued at</p>
          <div className="relative">
            <div className="flex flex-col gap-1">
              <Input
                className="text-right hide-arrow pl-6"
                type="number"
                name="price"
                id="price"
                min={0}
                maxLength={9}
                placeholder="50,000"
                value={equityInfo.price}
                onChange={(e) => {
                  const currentValue = e.target.value;
                  if (currentValue.includes('.')) {
                    const [intPart, decimalPart] = currentValue.split('.');
                    if (decimalPart.length > 2) return;
                    const formattedIntPart = intPart.replace(/^0+/, '') || '0';
                    e.target.value = `${formattedIntPart}.${decimalPart}`;
                  } else {
                    // For non-decimal numbers, remove leading zeros but allow a single zero
                    e.target.value = currentValue.replace(/^0+/, '') || '0';
                  }
                  const newValue = parseFloat(e.target.value);

                  if (
                    !Number.isNaN(newValue) &&
                    newValue >= 0 &&
                    newValue <= 100000000
                  ) {
                    setEquityInfo((current) => ({
                      ...current,
                      price: newValue,
                    }));
                  }
                }}
              />
            </div>
            <span className="absolute top-1/2 -translate-y-1/2 left-3 select-none touch-none text-neutral-4">
              AED
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyValuationSection;
